import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ReplaySubject, Subject, take, takeUntil } from 'rxjs';
import { IDisplayableItem } from 'src/app/models/interfaces/displayable-item.interface';
import { MatSelectFilterComponent } from '../mat-select-filter/mat-select-filter.component';

@Component({
    selector: 'app-mat-filterable-select',
    templateUrl: './mat-filterable-select.component.html',
    styleUrls: ['./mat-filterable-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: MatFilterableSelectComponent,
            multi: true,
        },
    ],
    imports: [MatFormFieldModule, MatSelectModule, FormsModule, ReactiveFormsModule, MatSelectFilterComponent, MatOptionModule, AsyncPipe]
})
export class MatFilterableSelectComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
    constructor() {}

    /** control for the selected element */
    public elementCtrl: UntypedFormControl = new UntypedFormControl();

    /** control for the MatSelect filter keyword */
    public elementFilterCtrl: UntypedFormControl = new UntypedFormControl();

    @Input()
    public label: string;
    /** list of elements */
    @Input()
    public elements: IDisplayableItem[];

    @Input()
    public className: string;

    /** list of elements filtered by search keyword */
    public filteredElements: ReplaySubject<IDisplayableItem[]> = new ReplaySubject<IDisplayableItem[]>(1);

    @ViewChild('singleSelect') singleSelect: MatSelect;

    /** Subject that emits when the component has been destroyed. */
    private _onDestroy = new Subject<void>();

    ngOnInit() {
        // set initial selection
        //this.elementCtrl.setValue(this.elements[10]);

        // load the initial element list
        this.filteredElements.next(this.elements.slice());

        // listen for search field value changes
        this.elementFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
            this.filterElements();
        });
    }

    ngAfterViewInit() {
        this.setInitialValue();
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    /**
     * Sets the initial value after the filteredElements are loaded initially
     */
    private setInitialValue() {
        this.filteredElements.pipe(take(1), takeUntil(this._onDestroy)).subscribe(() => {
            // setting the compareWith property to a comparison function
            // triggers initializing the selection according to the initial value of
            // the form control (i.e. _initializeSelection())
            // this needs to be done after the filteredElements are loaded initially
            // and after the mat-option elements are available
            this.singleSelect.compareWith = (a: string | number, b: string | number) => a === b;
        });
    }

    private filterElements() {
        if (!this.elements) {
            return;
        }
        // get the search keyword
        let search = this.elementFilterCtrl.value;
        if (!search) {
            this.filteredElements.next(this.elements.slice());
            return;
        } else {
            search = search.toLowerCase();
        }
        // filter the elements
        this.filteredElements.next(this.elements.filter((element) => element.displayName.toLowerCase().indexOf(search) > -1));
    }

    onChange: (value?: string | number) => void;

    onTouch: () => void;

    // upon UI element value changes, this method gets triggered
    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    // upon touching the element, this method gets triggered
    registerOnTouched(fn: any) {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.elementCtrl.disable({ emitEvent: false });
            this.elementCtrl.markAsUntouched();
        } else {
            this.elementCtrl.enable();
        }
    }

    // this method sets the value programmatically
    writeValue(value: string | number) {
        console.log('writeValue', value);
        this.elementCtrl.setValue(value);
    }

    public onElementSelected(event: MatSelectChange): void {
        this.onChange(event.value);
    }
}
